import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { pickErr } from "@/lib/api";
import { notifyError, notifySuccess } from "@/lib/notify";
import DataTable from "@/components/DataTable";

export default function Schools() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Per-school admin state from DB.
  // Shape: { [schoolId]: Array<{ id, email, school_id, created_at }> }
  const [adminsBySchool, setAdminsBySchool] = useState({});
  const [adminsLoading, setAdminsLoading] = useState(false);

  // Create-admin modal state
  const [adminModal, setAdminModal] = useState(null); // { schoolId, schoolName }
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminError, setAdminError] = useState("");

  const loadAdmins = useCallback(async (schools) => {
    if (!Array.isArray(schools) || schools.length === 0) {
      setAdminsBySchool({});
      return;
    }
    setAdminsLoading(true);
    try {
      const results = await Promise.all(
        schools.map((s) =>
          api
            .get(`/schools/${s.id}/admins`)
            .then((r) => [s.id, Array.isArray(r.data) ? r.data : []])
            .catch(() => [s.id, []])
        )
      );
      const map = {};
      for (const [sid, arr] of results) map[sid] = arr;
      setAdminsBySchool(map);
    } finally {
      setAdminsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/schools");
      const schools = Array.isArray(data) ? data : [];
      setList(schools);
      await loadAdmins(schools);
    } catch (err) {
      setError(pickErr(err, "Failed to load schools"));
    } finally {
      setLoading(false);
    }
  }, [loadAdmins]);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate(e) {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      await api.post("/schools", {
        name: name.trim(),
        address: address.trim() || null,
      });
      setName("");
      setAddress("");
      notifySuccess("School created");
      await load();
    } catch (err) {
      const msg = pickErr(err, "Failed to create school");
      setFormError(msg);
      notifyError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(id) {
    if (!window.confirm("Delete this school?")) return;
    try {
      await api.delete(`/schools/${id}`);
      notifySuccess("School deleted");
      // Forget any tracked admin state for this school.
      setAdminsBySchool((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await load();
    } catch (err) {
      const msg = pickErr(err, "Failed to delete school");
      setError(msg);
      notifyError(msg);
    }
  }

  function openAdminModal(school) {
    setAdminModal({ schoolId: school.id, schoolName: school.name });
    setAdminEmail("");
    setAdminPassword("");
    setAdminError("");
  }

  function closeAdminModal() {
    if (adminBusy) return;
    setAdminModal(null);
    setAdminEmail("");
    setAdminPassword("");
    setAdminError("");
  }

  async function onCreateAdmin(e) {
    e.preventDefault();
    if (!adminModal) return;
    setAdminError("");
    setAdminBusy(true);
    try {
      const { data } = await api.post(
        `/schools/${adminModal.schoolId}/create-admin`,
        {
          email: adminEmail.trim().toLowerCase(),
          password: adminPassword,
        }
      );
      const created = {
        id: data?.id,
        email: data?.email || adminEmail.trim().toLowerCase(),
        school_id: adminModal.schoolId,
        created_at: new Date().toISOString(),
      };
      setAdminsBySchool((prev) => {
        const existing = prev[adminModal.schoolId] || [];
        return {
          ...prev,
          [adminModal.schoolId]: [...existing, created],
        };
      });
      notifySuccess(`School admin created: ${created.email}`);
      setAdminModal(null);
      setAdminEmail("");
      setAdminPassword("");
    } catch (err) {
      const status = err?.response?.status;
      const msg = pickErr(err, "Failed to create school admin");
      setAdminError(
        status === 409 ? "This email is already registered." : msg
      );
      notifyError(msg);
    } finally {
      setAdminBusy(false);
    }
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Schools</h2>
      <p className="text-sm text-slate-500 mb-6">
        Create, view and delete schools.
      </p>

      <section className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">
          Add a school
        </h3>
        <form
          onSubmit={onCreate}
          className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end"
          data-testid="schools-create-form"
        >
          <div>
            <label className="block text-xs text-slate-600 mb-1">Name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="schools-create-name"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">
              Address (optional)
            </label>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              data-testid="schools-create-address"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="schools-create-submit"
            className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
          >
            {submitting ? "Creating…" : "Create"}
          </button>
        </form>
        {formError ? (
          <p data-testid="schools-create-error" className="mt-3 text-sm text-red-600">
            {formError}
          </p>
        ) : null}
      </section>

      <section>
        {(() => {
          const columns = [
            {
              key: "name",
              label: "Name",
              className: "text-slate-900",
            },
            {
              key: "address",
              label: "Address",
              accessor: (r) => r.address || "",
              render: (r) => r.address || "—",
              className: "text-slate-600",
            },
            {
              key: "admins",
              label: "School Admin",
              // Sort/search by first admin email if present.
              accessor: (r) => (adminsBySchool[r.id]?.[0]?.email || ""),
              sortable: true,
              exportable: true,
              render: (r) => {
                const admins = adminsBySchool[r.id] || [];
                if (admins.length > 0) {
                  return (
                    <ul
                      className="space-y-0.5"
                      data-testid={`schools-admin-cell-${r.id}`}
                    >
                      {admins.map((a) => (
                        <li
                          key={a.id || a.email}
                          className="flex items-center gap-2"
                        >
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          <span
                            className="text-slate-800"
                            data-testid={`schools-admin-email-${r.id}`}
                          >
                            {a.email}
                          </span>
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (adminsLoading)
                  return (
                    <span className="text-slate-400 text-xs">Loading…</span>
                  );
                return <span className="text-slate-400">—</span>;
              },
            },
          ];
          const actions = (s) => {
            const hasAdmin = (adminsBySchool[s.id] || []).length > 0;
            return (
              <div className="flex items-center justify-end gap-3">
                <Link
                  to={`/admin/schools/${s.id}`}
                  data-testid={`schools-view-edit-${s.id}`}
                  className="text-xs font-medium text-indigo-700 hover:text-indigo-900"
                >
                  View / Edit
                </Link>
                <button
                  onClick={() => openAdminModal(s)}
                  data-testid={`schools-create-admin-${s.id}`}
                  className="text-xs font-medium text-slate-700 hover:text-slate-900"
                >
                  {hasAdmin ? "Add Admin" : "Create Admin"}
                </button>
                <button
                  onClick={() => onDelete(s.id)}
                  data-testid={`schools-delete-${s.id}`}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  Delete
                </button>
              </div>
            );
          };
          return (
            <DataTable
              testid="schools"
              columns={columns}
              rows={list}
              loading={loading}
              error={error}
              getRowId={(r) => r.id}
              emptyMessage="No schools yet."
              defaultSort={{ key: "name", dir: "asc" }}
              exportFilename="schools"
              actions={actions}
            />
          );
        })()}
      </section>

      {/* ---------- Create School Admin Modal ---------- */}
      {adminModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          data-testid="schools-admin-modal"
          onClick={closeAdminModal}
          role="presentation"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg bg-white shadow-lg"
          >
            <div className="px-5 py-4 border-b border-slate-200">
              <h4 className="text-sm font-semibold text-slate-900">
                Create School Admin
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                For{" "}
                <span className="text-slate-700">
                  {adminModal.schoolName}
                </span>
              </p>
            </div>

            <form onSubmit={onCreateAdmin} className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  data-testid="schools-admin-modal-email"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  data-testid="schools-admin-modal-password"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              {adminError ? (
                <p
                  data-testid="schools-admin-modal-error"
                  className="text-sm text-red-600"
                >
                  {adminError}
                </p>
              ) : null}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeAdminModal}
                  disabled={adminBusy}
                  data-testid="schools-admin-modal-cancel"
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adminBusy}
                  data-testid="schools-admin-modal-submit"
                  className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-1.5 hover:bg-slate-800 disabled:opacity-60"
                >
                  {adminBusy ? "Creating…" : "Create Admin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
